import type { GripState, ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, hasMessage, t } from '@ctcells/i18n'

import { findModule, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 6축 로봇 이송 띠의 «읽기» — 응답(`GET /modules` · 구독 `module_status`)에서 그릴 값만 뽑는다.
 *
 * 🔴 **표시 파생이지 상태 추론이 아니다.** 버튼 · 명령 가능 여부를 만들지 않는다(이 띠에는 버튼이 없다).
 * 🔴 **응답에 없는 것은 지어내지 않는다**(2026-10-06 dev2 실측 · 히나):
 *    - 로봇의 «지금 위치»(어느 정거장 · 어느 자리 위) — module-core 장부(`arm_track.State{arm, point, aux}`)에는 있지만
 *      `/status` 에 안 싣는다 ⇒ 마커를 그리지 않는다. 종전 `railStopLeft()` 처럼 «모르면 파킹»으로 접지 않는다
 *    - 실행의 출발 · 도착 자리 — `current_execution` 에 `sequence` 이름 · `step` · `total` 만 있고 파라미터(`point_id`)가 없다
 *    - 집은 물건(어느 트레이) — `grips[].state` 는 «쥐었다/빈손»까지만 말한다
 *    - 관절각 · TCP — `telemetry` 가 `{grip_drop_policy}` 하나뿐이다
 *    ⇒ 셋 다 `docs/backend-requests.csv` 에 요청으로 올렸다. 값이 오면 그때 «그 모양대로» 읽는다(추측해 키를 미리 읽지 않는다)
 */

/** 띠의 정거장 — 왼쪽부터 원심분리기 · 어셈블러 · 염색기(정본 `positions.yaml` 정거장 셋 · 와이어프레임 1595행 순서) */
export const ROBOT_STATIONS = ['cent', 'asm', 'stain'] as const satisfies readonly Exclude<
  MonitoringDeviceKey,
  'robot'
>[]
export type RobotStationKey = (typeof ROBOT_STATIONS)[number]

/** 핸드오프 자리 한 칸 — `handoff_occupancy[]` 실물 모양 `{point_id, occupied, basis, updated_at}`(2026-10-06 dev2) */
export interface HandoffSpot {
  /** 서버 원값(`centrifuge.slide_rear`) */
  pointId: string
  /** 모듈 접두를 뗀 꼬리(`slide_rear` · `tray-1`) — 이름표 사전의 키 */
  tail: string
  occupied: boolean
}

export interface RobotStation {
  key: RobotStationKey
  /** 그 모듈의 스냅샷. 없으면 모듈이 목록에 없다(미등록 · 폐기) */
  snapshot: ModuleSnapshot | undefined
  /**
   * 그 모듈이 «발행한» 자리들.
   * 🔴 `undefined` = **미발행**(칸이 `null` · 모양을 못 읽음) — 「빈 자리 0개」와 다르다. 화면은 사유를 적는다.
   * ⚠ 서버는 «행이 있는» 자리만 싣는다(module-core `_점유를_싣는다` — 신고가 없던 자리를 `false` 로 채우지 않는다).
   */
  spots: readonly HandoffSpot[] | undefined
}

/**
 * 한 모듈의 핸드오프 자리 점유. 계약이 이 칸을 자유 객체(`anyOf [{}, null]`)로 두어 칸별로 읽는다.
 * 🔴 `occupied` 가 불리언이 아니면 그 행을 버린다 — 「모름」을 「빔」으로 접지 않는다.
 */
export function handoffSpots(snapshot: ModuleSnapshot | undefined): HandoffSpot[] | undefined {
  const raw: unknown = snapshot?.handoff_occupancy
  if (!Array.isArray(raw)) return undefined
  const prefix = snapshot === undefined ? '' : `${snapshot.module_type}.`
  const spots: HandoffSpot[] = []
  for (const entry of raw as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue
    const { point_id: pointId, occupied } = entry as { point_id?: unknown; occupied?: unknown }
    if (typeof pointId !== 'string' || pointId === '' || typeof occupied !== 'boolean') continue
    spots.push({
      pointId,
      tail: pointId.startsWith(prefix) ? pointId.slice(prefix.length) : pointId,
      occupied,
    })
  }
  /* 이름 순 — 서버 순서는 장부 행 순서라 갱신마다 흔들릴 수 있다. 자리가 띠 위에서 뛰지 않게 한다 */
  return spots.toSorted((a, b) => a.pointId.localeCompare(b.pointId))
}

/**
 * 자리 꼬리의 이름표(`slide_rear` → 「슬라이드 후면」). 사전에 없으면 **원값 그대로** — 이름을 지어내지 않는다.
 * ⚠ 사전 키 끝 마디는 `-` 를 못 받아(`i18n.test` 사전 규율) `tray-1` 을 `tray_1` 로 바꿔 찾는다. 바꿔 읽는 곳은 여기 하나다.
 */
export function handoffPointLabel(tail: string): string {
  const key = `monitoring.robot.point.${tail.replaceAll('-', '_')}`
  return hasMessage(key) ? t(key) : tail
}

export function robotStations(modules: readonly ModuleSnapshot[]): RobotStation[] {
  return ROBOT_STATIONS.map((key) => {
    const snapshot = findModule(modules, key)
    return { key, snapshot, spots: handoffSpots(snapshot) }
  })
}

/** 지금 도는 실행 — `current_execution` 그대로(1Hz `module_status` 미러). 끝난 실행은 모듈이 싣지 않는다 */
export interface RobotRun {
  /** 시퀀스 이름 원값(`move_to_module` · `grip` …). 모듈이 이름을 모르면 키째 빠진다 */
  sequence: string | undefined
  step: number | undefined
  total: number | undefined
}

export function robotRun(snapshot: ModuleSnapshot | undefined): RobotRun | undefined {
  const run = snapshot?.current_execution
  if (run == null) return undefined
  return {
    sequence: typeof run.sequence === 'string' && run.sequence !== '' ? run.sequence : undefined,
    step: typeof run.step === 'number' ? run.step : undefined,
    total: typeof run.total === 'number' ? run.total : undefined,
  }
}

/**
 * 그리퍼 파지 상태 — 🔴 **「미발행」과 「그리퍼 없음」은 다르다**(계약 `ModuleSnapshot.grips` 설명).
 * `undefined` = 칸이 없다(판정 불가) · 빈 배열 = 그리퍼가 없다.
 * ⚠ 이 값은 센서가 아니라 집기 · 놓기가 끝난 «기억»이다(module_service `/grips/forget` 설명) — 화면 문구도 그 이상을 말하지 않는다.
 */
export function robotGrips(
  snapshot: ModuleSnapshot | undefined,
): readonly { robotId: string; state: GripState }[] | undefined {
  const grips = snapshot?.grips
  if (!Array.isArray(grips)) return undefined
  return grips.map((grip) => ({ robotId: grip.robot_id, state: grip.state }))
}

/** 그리퍼 한 줄 — 미발행 · 없음 · 로봇별 상태(어느 그리퍼인지는 서버 값 `robot_id` 그대로) */
export function gripText(grips: ReturnType<typeof robotGrips>): string {
  if (grips === undefined) return t('monitoring.robot.grips.unknown')
  if (grips.length === 0) return t('monitoring.robot.grips.none')
  return grips
    .map((grip) => `${grip.robotId} ${enumLabel('enum.gripState', grip.state)}`)
    .join(' · ')
}

/** 실행 한 줄 — 「모듈로 이동 · 4/5」. 이름을 모르면 「실행 중」, 실행이 없으면 「실행 없음」 */
export function runText(run: RobotRun | undefined): string {
  if (run === undefined) return t('monitoring.robot.run.none')
  const name =
    run.sequence === undefined
      ? t('monitoring.robot.run.active')
      : enumLabel('enum.robotSequence', run.sequence)
  return run.step === undefined || run.total === undefined
    ? name
    : `${name} · ${t('event.content.stepCount', { step: run.step, total: run.total })}`
}
